import { useState } from "react";
import { NEWS, SOURCE_TABS } from "@/mocks/content";

const SOURCE_STYLE: Record<string, { pill: string; dot: string }> = {
  知情人士: { pill: "bg-primary-100 text-primary-700", dot: "bg-primary-500" },
  狗仔线索: { pill: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
  记者报道: { pill: "bg-secondary-200 text-secondary-800", dot: "bg-secondary-600" },
  官方回应: { pill: "bg-foreground-900 text-foreground-100", dot: "bg-foreground-500" },
};

export default function LatestNews() {
  const [active, setActive] = useState<(typeof SOURCE_TABS)[number]>("全部");
  const tabs = ["全部", ...SOURCE_TABS] as const;
  const list = active === "全部" ? NEWS : NEWS.filter((n) => n.source === active);
  const top3 = NEWS.slice(0, 3);

  return (
    <section id="latest" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 text-sm font-bold tracking-[0.25em] text-primary-600">LIVE · 信源标注</p>
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">实时滚动 · 按来源区分</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              每条动态都标注来自知情人士、狗仔线索、记者报道还是官方回应——来源决定了它该被相信几分。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* tabs + list */}
          <div className="lg:col-span-2">
            <div className="mb-5 inline-flex max-w-full flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {tabs.map((t) => {
                const count = t === "全部" ? NEWS.length : NEWS.filter((n) => n.source === t).length;
                const isActive = active === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActive(t)}
                    className={
                      "flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
                      (isActive
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-50")
                    }
                  >
                    {t}
                    <span
                      className={
                        "rounded-full px-1.5 text-xs " +
                        (isActive ? "bg-white/25" : "bg-background-200 text-foreground-600")
                      }
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {list.map((n) => {
                const st = SOURCE_STYLE[n.source] ?? SOURCE_STYLE["知情人士"];
                return (
                  <a
                    key={n.title}
                    href="#source"
                    className="group flex flex-col gap-3 p-5 transition-colors hover:bg-background-100 md:flex-row md:items-start"
                  >
                    <div className="flex items-center gap-2 md:w-32 md:shrink-0 md:flex-col md:items-start md:gap-1.5">
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-semibold">
                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`}></span>
                        {n.source}
                      </span>
                      <span className="hidden text-xs text-foreground-500 md:inline">{n.time}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-base leading-snug font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                          {n.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{n.summary}</p>
                      <div className="mt-2.5 flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${st.pill}`}>
                          <i className="ri-price-tag-3-line mr-1"></i>
                          {n.tag}
                        </span>
                        <span className="inline-flex items-center whitespace-nowrap text-xs text-foreground-400 md:hidden">
                          <i className="ri-time-line mr-1"></i> {n.time}
                        </span>
                        <span className="inline-flex items-center whitespace-nowrap text-xs text-foreground-400">
                          <i className="ri-eye-line mr-1"></i> {n.reads} 阅读
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>

          {/* ranking rail */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <div className="flex items-center justify-between border-b border-background-200 bg-background-100 px-5 py-3.5">
                <span className="font-heading text-base font-black text-foreground-950">热议排行</span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                  <i className="ri-fire-fill"></i> 热
                </span>
              </div>
              <ol className="flex flex-col">
                {top3.map((n, i) => (
                  <li key={n.title} className="group border-b border-background-200/70 last:border-0">
                    <a href="#latest" className="flex gap-4 p-4 transition-colors hover:bg-background-50">
                      <span className="font-heading text-2xl font-black text-primary-200 transition-colors group-hover:text-primary-500">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <p className="text-sm leading-snug font-semibold text-foreground-900 line-clamp-2 transition-colors group-hover:text-primary-700">
                          {n.title}
                        </p>
                        <p className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                          <span className="whitespace-nowrap">{n.source}</span>
                          <span className="flex items-center whitespace-nowrap">
                            <i className="ri-eye-line mr-1"></i> {n.reads}
                          </span>
                        </p>
                      </div>
                    </a>
                  </li>
                ))}
              </ol>
              <a
                href="#subscribe"
                className="flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                订阅每天深挖线索 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}